import { useEffect, useState } from 'react';
import type { FeedbackCategory, FeedbackDiagnostics } from '../../../shared/protocol.ts';
import { account } from '../lib/account.ts';
import { api } from '../lib/api.ts';
import { Observable } from '../lib/emitter.ts';
import { Icon, useObservable } from './common.tsx';

/** Each kind of feedback, with the question its message box asks. */
export const FEEDBACK_CATEGORIES: [FeedbackCategory, string, string][] = [
  ['problem', 'Something’s broken', 'What happened, and what did you expect?'],
  ['idea', 'Idea or request', 'What would you like Wordstow to do?'],
  ['question', 'Question', 'What would you like to know?'],
  ['praise', 'Praise', 'What worked well?'],
  ['other', 'Other', 'What’s on your mind?'],
];

type StudioDescription = FeedbackDiagnostics['studio'];

/** Whether the feedback panel is open, where its button is, and what a report can say about the studio. */
class FeedbackUi extends Observable {
  open = false;
  /** In a studio's live room the button is in the top bar, clear of the call controls. */
  inRoom = false;
  /** On a studio's pages: who this person is there and what's going on, for the diagnostics. */
  studio: (() => StudioDescription) | null = null;

  show() {
    this.open = true;
    this.changed();
  }
  hide() {
    this.open = false;
    this.changed();
  }
  /** For an effect: returns its cleanup. */
  enterRoom() {
    this.inRoom = true;
    this.changed();
    return () => {
      this.inRoom = false;
      this.changed();
    };
  }
  describeStudio(describe: () => StudioDescription) {
    this.studio = describe;
    return () => {
      if (this.studio === describe) this.studio = null;
    };
  }
}
export const feedbackUi = new FeedbackUi();

// What's been typed survives closing the panel by accident.
const draft = { category: 'problem' as FeedbackCategory, message: '', email: '' };

/** Where the report came from, without the studio key a link carries. */
const pageUrl = () => (location.pathname + location.search).replace(/([?&]k=)[^&#]+/g, '$1…');

function collectDiagnostics(): FeedbackDiagnostics {
  return {
    lines: (((window as any).__takehouseLog as string[] | undefined) ?? []).slice(-300),
    screen: `${screen.width}×${screen.height} screen, ${innerWidth}×${innerHeight} window, ${devicePixelRatio}× pixels`,
    studio: feedbackUi.studio?.() ?? undefined,
  };
}

/** The floating Feedback button (except in a studio's live room, which has its own in the top bar) and the panel. */
export function Feedback() {
  useObservable(feedbackUi);
  return (
    <>
      {!feedbackUi.inRoom && !feedbackUi.open && (
        <button className="feedback-launcher" onClick={() => feedbackUi.show()}>
          <Icon name="chat" size={15} /> Feedback
        </button>
      )}
      {feedbackUi.open && <FeedbackPanel />}
    </>
  );
}

function FeedbackPanel() {
  useObservable(account);
  const me = account.account;
  const inRoom = feedbackUi.inRoom;
  const [category, setCategory] = useState(draft.category);
  const [message, setMessage] = useState(draft.message);
  const [email, setEmail] = useState(draft.email);
  const [withDiagnostics, setWithDiagnostics] = useState(true);
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState('');
  Object.assign(draft, { category, message, email });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && feedbackUi.hide();
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);

  const send = async () => {
    if (message.trim().length < 3) return setError('Tell us a bit more, at least a few words');
    setState('sending');
    setError('');
    let name: string | undefined;
    try {
      name = localStorage.getItem('takehouse.name') || undefined;
    } catch {}
    try {
      await api.sendFeedback({
        category,
        message: message.trim(),
        pageUrl: pageUrl(),
        pageTitle: document.title,
        ...(me ? {} : { email: email.trim() || undefined, name }),
        diagnostics: withDiagnostics ? collectDiagnostics() : undefined,
      });
      Object.assign(draft, { message: '', email: '' });
      setState('sent');
      setTimeout(() => feedbackUi.hide(), 1800);
    } catch (e) {
      setError((e as Error).message);
      setState('idle');
    }
  };

  const ask = FEEDBACK_CATEGORIES.find(([id]) => id === category)![2];
  return (
    <div className={`feedback-back ${inRoom ? 'in-room' : ''}`} onMouseDown={(e) => e.target === e.currentTarget && feedbackUi.hide()}>
      <div className="feedback-panel" role="dialog" aria-modal="true" aria-label="Send feedback">
        <header>
          <h2 className="grow">Send feedback</h2>
          <button className="ghost icon sm" onClick={() => feedbackUi.hide()} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </header>
        {state === 'sent' ? (
          <div className="feedback-sent">
            <span className="ready-icon">
              <Icon name="check" />
            </span>
            <div className="col" style={{ gap: 2 }}>
              <strong>Thanks, we’ve got it.</strong>
              <span className="muted small">We read every message{me || email.trim() ? ', and we’ll reply if we need to know more' : ''}.</span>
            </div>
          </div>
        ) : (
          <>
            <div className="option-chips" role="radiogroup" aria-label="What kind of feedback?">
              {FEEDBACK_CATEGORIES.map(([id, label]) => (
                <button key={id} role="radio" aria-checked={category === id} className={category === id ? 'on' : ''} onClick={() => setCategory(id)}>
                  {label}
                </button>
              ))}
            </div>
            <textarea rows={5} maxLength={4000} autoFocus aria-label="Your message" placeholder={ask} value={message} onChange={(e) => setMessage(e.target.value)} />
            {me ? (
              <span className="muted small">Sending as {me.name ? `${me.name} (${me.email})` : me.email}</span>
            ) : (
              <input type="email" autoComplete="email" placeholder="Email (optional, so we can reply)" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            )}
            <label className="check-row">
              <input type="checkbox" checked={withDiagnostics} onChange={(e) => setWithDiagnostics(e.target.checked)} />
              <span>
                Include diagnostics from this {inRoom ? 'call' : 'page'}
                <span className="field-hint">
                  Your browser{feedbackUi.studio ? ', devices' : ''} and the app’s recent log, to help us see what went wrong.
                </span>
              </span>
            </label>
            {error && (
              <div className="alert bad small">
                <Icon name="warn" size={15} /> {error}
              </div>
            )}
            <footer>
              <span className="faint small mono ellipsis grow">Sent from {location.pathname}</span>
              <button className="primary" onClick={() => void send()} disabled={state === 'sending'}>
                {state === 'sending' && <span className="spinner" />} Send
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
